import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { usePortfolio } from '../../context/PortfolioContext';

import {
  getTransactionEditConstraints,
  previewTransactionEdit,
  patchV2Transaction
} from '../../composables/fetchAPI';
import ManageCashModal from './ManageCashModal'
import { sanitizeDecimalInput, validateCryptoDecimal } from '../../utils/cryptoDecimal';
interface EditTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  transaction: any;
  onSuccess: (result: any) => void;
  onNeedsFix: (needsFixData: any) => void;
  // พอร์ตคริปโต: ส่งเฉพาะช่องที่แก้ใน decimalValues เป็น string ตรงตัว ห้ามส่ง Number ปน (จะทับค่าจริง)
  isCrypto?: boolean;
}

const FINANCIAL_FIELDS = ['quantity', 'price', 'fee', 'tax'] as const;
const FIELD_LABEL: Record<string, string> = { quantity: 'จำนวน', price: 'ราคา', fee: 'ค่าธรรมเนียม', tax: 'ภาษี' };

const EditTransactionModal: React.FC<EditTransactionModalProps> = ({
  isOpen,
  onClose,
  portfolioId,
  transaction,
  onSuccess,
  onNeedsFix,
  isCrypto = false
}) => {
  const [isLoadingConstraints, setIsLoadingConstraints] = useState(false);
  const [constraintsData, setConstraintsData] = useState<any>(null);
  const {refreshUserPortfolios, userPortfolios, adminPortfolios, } = usePortfolio();

  // เพิ่ม State สำหรับจัดการ Modal จัดการเงินสด
  const [isCashModalOpen, setIsCashModalOpen] = useState(false);
  const [cashModalInitialDate, setCashModalInitialDate] = useState<string>('');
  const [cashModalInitialAmount, setCashModalInitialAmount] = useState<number>(0);
  // ฟอร์มข้อมูลที่จะแก้ไข
  const [formData, setFormData] = useState({
    transactionDate: '', // 👈 เพิ่มฟิลด์วันที่
    quantity: '',
    price: '',
    fee: '',
    tax: '',
    note: ''
  });
  // ค่าเริ่มต้นตอนเปิด Modal ไว้เทียบว่ามีการแก้ไขหรือยัง
  const [initialFormData, setInitialFormData] = useState(formData);

  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null); // เก็บผล Preview

useEffect(() => {
  if (isOpen && transaction) {
    // รองรับทั้งกรณีส่ง transaction ตรงๆ หรือส่งแบบมี .transaction ครอบ
    const txn = transaction.transactionId ? transaction : (transaction.transaction || transaction);
let formattedDate = '';
    if (txn.transactionDate) {
      formattedDate = new Date(txn.transactionDate).toISOString().split('T')[0];
    }
    // คริปโต: ค่าตรงตัวอยู่ใน decimalValues (field เดิมเป็นแค่ตัวเลขสำรองที่อาจถูกปัด)
    const exact = isCrypto ? (txn.decimalValues || {}) : {};
    const initial = {
      transactionDate: formattedDate,
      quantity: String(exact.quantity ?? txn.quantity ?? txn.eligibleQuantity ?? ''),
      price: String(exact.price ?? txn.price ?? txn.cashAmountPerShare ?? ''),
      fee: String(exact.fee ?? txn.fee ?? ''),
      tax: String(exact.tax ?? txn.tax ?? txn.taxAmount ?? ''),
      note: txn.note ?? ''
    };
    setFormData(initial);
    setInitialFormData(initial);
    setPreviewData(null);
    fetchConstraints(txn.transactionId);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isOpen, transaction, isCrypto]);

  const fetchConstraints = async (txnId: string) => {
    setIsLoadingConstraints(true);
    try {
      const res = await getTransactionEditConstraints(portfolioId, txnId);
      setConstraintsData(res);
    } catch (error) {
      console.error('Error fetching edit constraints:', error);
      setConstraintsData({ patchable: true, editableFields: ['quantity', 'price', 'fee', 'tax', 'note'] });
    } finally {
      setIsLoadingConstraints(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setPreviewData(null); // รีเซ็ต Preview ทุกครั้งที่มีการเปลี่ยนค่า
  };

  // คืน payload หรือ null ถ้าตัวเลขคริปโตไม่ถูกต้อง (แจ้ง toast ให้แล้ว)
  const buildPayload = (): any | null => {
    const payload: any = {};
    if (formData.transactionDate) {
      // ส่งเป็น ISO String หรือรูปแบบที่ Backend รองรับ (เช่น "2026-08-01T00:00:00.000Z")
      payload.transactionDate = new Date(formData.transactionDate).toISOString();
    }
    if (isCrypto) {
      const decimalValues: Record<string, string> = {};
      for (const field of FINANCIAL_FIELDS) {
        const value = String(formData[field] ?? '').trim();
        if (value === String(initialFormData[field] ?? '').trim()) continue; // ส่งเฉพาะช่องที่แก้
        const isCharge = field === 'fee' || field === 'tax';
        const err = validateCryptoDecimal(value || (isCharge ? '0' : ''), { allowZero: isCharge });
        if (err) {
          toast.error(`${FIELD_LABEL[field]}: ${err}`);
          return null;
        }
        decimalValues[field] = value || '0';
      }
      if (Object.keys(decimalValues).length > 0) payload.decimalValues = decimalValues;
    } else {
      if (formData.quantity !== '') payload.quantity = Number(formData.quantity);
      if (formData.price !== '') payload.price = Number(formData.price);
      if (formData.fee !== '') payload.fee = Number(formData.fee);
      if (formData.tax !== '') payload.tax = Number(formData.tax);
    }
    if (formData.note !== '') payload.note = formData.note;
    return payload;
  };

  // 1. ปุ่ม Preview Impact (POST edit-preview)
  const handlePreview = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setIsProcessing(true);
    try {
      const res = await previewTransactionEdit(portfolioId, transaction.transactionId, payload);
      setPreviewData(res);
    } catch (error: any) {
      toast.error(error.message || 'ไม่สามารถพรีวิวการแก้ไขได้');
    } finally {
      setIsProcessing(false);
    }
  };

  // 2. ปุ่ม Commit Save / Save Anyway (PATCH)
  const handleSubmit = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setIsProcessing(true);
    try {
      const res = await patchV2Transaction(portfolioId, transaction.transactionId, payload);

      // ตรวจสอบสถานะ Chain ตามสเปก
      if (res.chainStatus === 'NEEDS FIX') {
        toast.success('บันทึกการแก้ไขแล้ว แต่ Portfolio จำเป็นต้องแก้ไขต่อ');
        onNeedsFix(res);
      } else {
        toast.success('อัปเดตรายการและ Chain สำเร็จ (VALID)');
        onSuccess(res);
      }
      refreshUserPortfolios()
      onClose();
    } catch (error: any) {
      toast.error(error.th_message || error.message || 'เกิดข้อผิดพลาดในการบันทึก');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen || !transaction) return null;

  // หุ้น: type=number เหมือนเดิม / คริปโต: ช่องข้อความรับทศนิยมยาว (ไม่ผ่าน Number)
  const decimalInputProps = (field: typeof FINANCIAL_FIELDS[number]) => isCrypto
    ? {
        type: 'text',
        inputMode: 'decimal' as const,
        autoComplete: 'off',
        value: formData[field],
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(field, sanitizeDecimalInput(e.target.value)),
      }
    : {
        type: 'number',
        step: 'any',
        value: formData[field],
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(field, e.target.value),
      };

  const editableFields = constraintsData?.editableFields || ['transactionDate','quantity', 'price', 'fee', 'tax', 'note'];
  const qtyConstraint = constraintsData?.constraints?.quantity;
  const chainStatus = previewData?.data?.chainValidation?.chainStatus || previewData?.chainStatus;
  // เทียบแบบ String เพราะค่าเริ่มต้นอาจเป็น number แต่ input คืนค่าเป็น string
  const isDirty = (Object.keys(formData) as (keyof typeof formData)[]).some(
    (key) => String(formData[key] ?? '') !== String(initialFormData[key] ?? '')
  );
  const canPreview = !isProcessing && !isLoadingConstraints && isDirty;
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fadeIn">
      {/* 🌟 ปรับกว้างขึ้นเป็น max-w-5xl เพื่อให้แบ่งฝั่งซ้าย-ขวาได้แบบเป๊ะๆ */}
      <div className="bg-gray-900 border border-gray-700 w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden flex flex-col text-white">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-gray-850">
          <div>
            <h3 className="text-lg font-bold text-yellow-400">แก้ไขรายละเอียดรายการ (Edit Transaction)</h3>
            <p className="text-xs text-gray-400">ID: {transaction.transactionId} | Ticker: {transaction.symbol || transaction.assetSymbol || '-'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl font-bold px-2">&times;</button>
        </div>

        {/* Body: แบ่ง Grid เป็น 2 คอลัมน์ซ้ายขวาตั้งแต่เริ่ม */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-y-auto max-h-[75vh]">

          {/* 📦 ฝั่งซ้าย: ฟอร์มแก้ไขข้อมูล */}
          <div className="bg-gray-950/40 border border-gray-800 rounded-xl p-5 space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-gray-300 border-b border-gray-800 pb-2">รายละเอียดข้อมูล (Transaction Form)</h4>

              {isLoadingConstraints ? (
                <div className="py-10 flex flex-col items-center justify-center gap-4">
                  <style>{`
                    @keyframes etm-hourglass-rotate {
                      0%, 40% { transform: rotate(0deg); }
                      50%, 90% { transform: rotate(180deg); }
                      100% { transform: rotate(360deg); }
                    }
                    @keyframes etm-hourglass-sand-top {
                      0% { transform: scaleY(1); }
                      80%, 100% { transform: scaleY(0); }
                    }
                    @keyframes etm-hourglass-sand-bottom {
                      0% { transform: scaleY(0); }
                      80%, 100% { transform: scaleY(1); }
                    }
                    @keyframes etm-hourglass-stream {
                      0%, 78% { opacity: 1; }
                      80%, 100% { opacity: 0; }
                    }
                    @keyframes etm-hourglass-glow {
                      0% { transform: scale(1); opacity: 0.45; }
                      100% { transform: scale(1.35); opacity: 0; }
                    }
                    @keyframes etm-hourglass-progress {
                      0% { transform: translateX(-100%); }
                      100% { transform: translateX(250%); }
                    }
                    .etm-hourglass-spin { animation: etm-hourglass-rotate 2.4s ease-in-out infinite; will-change: transform; }
                    .etm-hourglass-sand-top { transform-origin: 32px 30px; transform-box: view-box; animation: etm-hourglass-sand-top 1.2s linear infinite; }
                    .etm-hourglass-sand-bottom { transform-origin: 32px 54px; transform-box: view-box; animation: etm-hourglass-sand-bottom 1.2s linear infinite; }
                    .etm-hourglass-stream { animation: etm-hourglass-stream 1.2s linear infinite; }
                    .etm-hourglass-ring { animation: etm-hourglass-glow 2.4s ease-out infinite; will-change: transform, opacity; }
                    .etm-hourglass-bar { animation: etm-hourglass-progress 1.6s ease-in-out infinite; will-change: transform; }
                  `}</style>
                  {/* นาฬิกาทราย */}
                  <div className="relative w-20 h-20 rounded-full flex items-center justify-center bg-gray-800 border-2 border-yellow-400 text-yellow-400">
                    <span className="etm-hourglass-ring absolute inset-0 rounded-full border-2 border-yellow-400 pointer-events-none" />
                    <svg className="etm-hourglass-spin w-12 h-12" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                      {/* ตัวแก้ว */}
                      <path d="M18 9 C18 22 30 28 30 32 C30 36 18 42 18 55 H46 C46 42 34 36 34 32 C34 28 46 22 46 9 Z" fill="#111827" stroke="#d1d5db" strokeOpacity="0.55" strokeWidth="2.5" strokeLinejoin="round" />
                      {/* ทราย */}
                      <path className="etm-hourglass-sand-top" d="M21 13 H43 C42 20 35 26 32 30 C29 26 22 20 21 13 Z" fill="currentColor" />
                      <rect className="etm-hourglass-stream" x="31.25" y="30" width="1.5" height="22" fill="currentColor" />
                      <path className="etm-hourglass-sand-bottom" d="M32 44 C36 47 42 50 43 54 H21 C22 50 28 47 32 44 Z" fill="currentColor" />
                      {/* ฝาบน-ล่าง */}
                      <rect x="13" y="4" width="38" height="5" rx="2.5" fill="currentColor" />
                      <rect x="13" y="55" width="38" height="5" rx="2.5" fill="currentColor" />
                    </svg>
                  </div>

                  <div className="text-center">
                    <div className="text-sm font-bold text-gray-200">กำลังโหลดเงื่อนไขการแก้ไข</div>
                    <div className="mt-1 text-xs text-gray-400">อาจะใช้เวลาเวลามากกว่า 1 นาที หรือแล้วแต่ประวัติรายการ กรุณารอสักครู่...</div>
                  </div>

                  {/* แถบโหลด */}
                  <div className="w-full max-w-xs h-1.5 rounded-full overflow-hidden bg-gray-800">
                    <div className="etm-hourglass-bar h-full w-2/5 rounded-full bg-yellow-400" />
                  </div>
                </div>
              ) : (
                <>
                  {editableFields.includes('transactionDate') && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1">วันที่ทำรายการ (Transaction Date)</label>
                      <input
                        type="date"
                        value={formData.transactionDate}
                        onChange={(e) => handleChange('transactionDate', e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 text-white"
                      />
                    </div>
                  )}

                  {editableFields.includes('quantity') && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1">จำนวน (Quantity)</label>
                      <input
                        {...decimalInputProps('quantity')}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 text-white"
                      />
                      {qtyConstraint && (
                        <div className="text-[10px] text-gray-400 mt-1">
                          คำแนะนำช่วงค่า (Min: {qtyConstraint.min}, Max: {qtyConstraint.max})
                        </div>
                      )}
                    </div>
                  )}

                  {editableFields.includes('price') && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1">ราคา / ปันผลต่อหุ้น (Price)</label>
                      <input
                        {...decimalInputProps('price')}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 text-white"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    {editableFields.includes('fee') && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-300 mb-1">ค่าธรรมเนียม (Fee)</label>
                        <input
                          {...decimalInputProps('fee')}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 text-white"
                        />
                      </div>
                    )}
                    {editableFields.includes('tax') && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-300 mb-1">ภาษี (Tax)</label>
                        <input
                          {...decimalInputProps('tax')}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 text-white"
                        />
                      </div>
                    )}
                  </div>

                  {editableFields.includes('note') && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1">บันทึกย่อ (Note)</label>
                      <textarea
                        value={formData.note}
                        onChange={(e) => handleChange('note', e.target.value)}
                        rows={2}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 text-white"
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ปุ่ม Preview อยู่ฝั่งซ้ายล่างสุดของฟอร์ม */}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <button
                onClick={handlePreview}
                disabled={!canPreview}
                className={`w-full py-2.5 text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg ${
                  canPreview
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isProcessing
                  ? 'กำลังตรวจสอบ...'
                  : isLoadingConstraints
                    ? 'กำลังโหลดเงื่อนไข...'
                    : !isDirty
                      ? '✏️ แก้ไขข้อมูลก่อนจึงจะพรีวิวได้'
                      : '🔍 พรีวิวผลกระทบ (Preview Impact)'}
              </button>
            </div>
          </div>

          {/* 📦 ฝั่งขวา: กล่องแสดงผล Preview Impact เต็มพื้นที่ */}
          <div className="bg-gray-950/60 border border-gray-800 rounded-xl p-5 flex flex-col justify-between">
            <div>
              <div className="text-xs font-bold text-gray-400 mb-3 flex items-center justify-between border-b border-gray-800 pb-2">
                <span>ผลการตรวจสอบผลกระทบ (Preview Impact)</span>
                <span className="text-[10px] bg-gray-800 px-2 py-0.5 rounded text-gray-300">
                  {chainStatus || 'REQUIRED'}
                </span>
              </div>

              {!previewData ? (
                <div className="text-center py-24 text-gray-500 text-xs flex flex-col items-center justify-center space-y-2">
                  <span className="text-2xl">👉</span>
                  <span>กรุณากรอกข้อมูลและกดปุ่ม <span className="text-blue-400 font-semibold">"พรีวิวผลกระทบ"</span> เพื่อตรวจสอบความถูกต้องล่วงหน้าก่อนบันทึก</span>
                </div>
              ) : (
                <div className="space-y-4 text-xs">
                  <div className={`p-3 rounded-lg border ${
                    chainStatus === 'NEEDS_FIX' || chainStatus === 'NEEDS FIX'
                      ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300'
                      : 'bg-green-500/10 border-green-500/30 text-green-300'
                  }`}>
                    <div className="font-bold">
                      {chainStatus === 'VALID' ? '✅ พรีวิวสำเร็จ: Chain สมบูรณ์ (VALID)' : '⚠️ พรีวิวพบปัญหา: ต้องแก้ไขต่อ (NEEDS FIX)'}
                    </div>
                  </div>

                  {previewData.data?.chainValidation?.affectedTransactions?.length > 0 && (
                    <div className="space-y-2">
                      <div className="font-semibold text-yellow-400">รายการที่ได้รับผลกระทบ:</div>
                      {previewData.data.chainValidation.affectedTransactions.map((t: any, idx: number) => (
                        <div key={idx} className="bg-black/40 p-2.5 rounded border border-yellow-500/20 space-y-1">
                          <div className="font-bold text-yellow-300 flex justify-between">
                            <span>{t.type} ({t.ticker || t.errorCode})</span>
                            <span className="text-red-400">{t.errorCode}</span>
                          </div>
                          <div>{t.th_reason || t.reason}</div>

                          {t.errorCode === 'INSUFFICIENT_CASH' && (
                            <button
                              type="button"
                              onClick={() => {
                                const rawDate = t.transactionDate || transaction.transactionDate;
                                const targetDate = rawDate ? new Date(rawDate) : new Date();
                                targetDate.setDate(targetDate.getDate() - 1);
                                setCashModalInitialDate(targetDate.toISOString().split('T')[0]);
                                setCashModalInitialAmount(t.shortageCash || 250);
                                setIsCashModalOpen(true);
                              }}
                              className="w-full mt-2 py-1.5 bg-green-600 hover:bg-green-500 text-white font-bold rounded text-xs flex items-center justify-center gap-1"
                            >
                              💰 เติมเงินสดด่วน (${(t.shortageCash || 250).toLocaleString()} USD)
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {previewData.data?.transactionPreview && (
                    <div className="bg-black/30 p-3 rounded space-y-1 text-gray-300">
                      <div className="font-semibold text-yellow-400 mb-1">ค่าหลังพรีวิว (Transaction Preview):</div>
                      <div>จำนวน: {previewData.data.transactionPreview.quantity} | ราคา: ${previewData.data.transactionPreview.price}</div>
                      <div>ยอดรวมสุทธิ: ${previewData.data.transactionPreview.netAmount}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="text-[10px] text-gray-400 bg-gray-900/80 p-2.5 rounded border border-gray-800 text-center">
              ℹ️ นี่เป็นเพียงการพรีวิว ข้อมูลจะถูกบันทึกจริงก็ต่อเมื่อคุณกดปุ่มยืนยันด้านล่าง
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-800 flex justify-between items-center bg-gray-850">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-bold rounded transition-colors"
          >
            ยกเลิก
          </button>

          <button
            onClick={handleSubmit}
            disabled={isProcessing || !previewData}
            className={`px-6 py-2 text-sm font-bold rounded-lg transition-colors shadow-lg ${
              !previewData
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : chainStatus === 'NEEDS FIX' || chainStatus === 'NEEDS_FIX'
                  ? 'bg-yellow-600 hover:bg-yellow-500 text-white'
                  : 'bg-yellow-500 hover:bg-yellow-400 text-gray-950'
            }`}
          >
            {isProcessing ? 'กำลังบันทึก...' : chainStatus === 'NEEDS FIX' || chainStatus === 'NEEDS_FIX' ? 'บันทึกทั้งที่ Chain พัง (Save Anyway)' : 'ยืนยันบันทึก (Save)'}
          </button>
        </div>

        <ManageCashModal
          isOpen={isCashModalOpen}
          onClose={() => setIsCashModalOpen(false)}
          onSave={() => {
            setIsCashModalOpen(false);
            handlePreview();
          }}
          portfolioId={portfolioId}
          currentCash={0}
          isCrypto={isCrypto}
          initialDate={cashModalInitialDate}
          initialAmount={cashModalInitialAmount}
        />
      </div>
    </div>
  );
};

export default EditTransactionModal;
